const DAY_IN_MS = 24 * 60 * 60 * 1000;

export interface SubscriptionLike {
  subscription_plan?: string | null;
  subscription_days_remaining?: number | string | null;
  subscription_expires_at?: string | null;
  role?: string | null;
  is_super_admin?: boolean | null;
}

export type SubscriptionPlan = 'free' | 'starter' | 'pro';

export function normalizeSubscriptionPlan(value?: string | null): SubscriptionPlan {
  const normalized = String(value || 'free')
    .trim()
    .toLowerCase();
  if (normalized === 'pro') return 'pro';
  if (normalized === 'starter') return 'starter';
  return 'free';
}

export function getSubscriptionPlanLabel(plan?: string | null) {
  const normalized = normalizeSubscriptionPlan(plan);
  if (normalized === 'pro') return 'PRO';
  if (normalized === 'starter') return 'Starter';
  return 'Free';
}

function toTimestamp(value?: string | null) {
  if (!value) return null;
  const parsed = new Date(value);
  const time = parsed.getTime();
  return Number.isFinite(time) ? time : null;
}

export function getRemainingDaysFromExpiry(
  expiryValue?: string | null,
  nowInput: number | Date = Date.now()
) {
  const expiryMs = toTimestamp(expiryValue);
  if (!expiryMs) return 0;

  const nowMs = nowInput instanceof Date ? nowInput.getTime() : nowInput;
  const remainingMs = Math.max(0, expiryMs - nowMs);
  if (remainingMs <= 0) return 0;

  return Math.ceil(remainingMs / DAY_IN_MS);
}

export function getRemainingTimeParts(remainingMs: number) {
  if (remainingMs <= 0) {
    return { hours: 0, minutes: 0 };
  }

  const totalMinutes = Math.max(1, Math.ceil(remainingMs / (60 * 1000)));

  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
  };
}

export function extendSubscriptionExpiration(
  currentExpiryValue: string | null | undefined,
  daysToGrant: number,
  nowInput: number | Date = Date.now()
) {
  const safeDays = Math.max(0, Number(daysToGrant) || 0);
  const nowMs = nowInput instanceof Date ? nowInput.getTime() : nowInput;
  const currentExpiryMs = toTimestamp(currentExpiryValue);
  const baseMs = currentExpiryMs && currentExpiryMs > nowMs ? currentExpiryMs : nowMs;

  return new Date(baseMs + safeDays * DAY_IN_MS).toISOString();
}

export function isTrialExpiryWindow(
  currentExpiryValue: string | null | undefined,
  profileCreatedAtValue: string | null | undefined,
  trialDays = 5,
  toleranceMs = DAY_IN_MS
) {
  const currentExpiryMs = toTimestamp(currentExpiryValue);
  const profileCreatedAtMs = toTimestamp(profileCreatedAtValue);

  if (!currentExpiryMs || !profileCreatedAtMs) {
    return false;
  }

  const expectedTrialExpiryMs = profileCreatedAtMs + Math.max(0, trialDays) * DAY_IN_MS;
  return Math.abs(currentExpiryMs - expectedTrialExpiryMs) <= toleranceMs;
}

export function extendSubscriptionFromPaidPurchase(
  currentExpiryValue: string | null | undefined,
  daysToGrant: number,
  options: {
    purchaseTimeValue?: string | null;
    profileCreatedAtValue?: string | null;
    hadPreviousPaidOrder?: boolean;
    trialDays?: number;
    nowInput?: number | Date;
  } = {}
) {
  const purchaseMs =
    toTimestamp(options.purchaseTimeValue) ||
    (options.nowInput instanceof Date ? options.nowInput.getTime() : options.nowInput) ||
    Date.now();
  const shouldResetFromPurchaseTime =
    options.hadPreviousPaidOrder !== true &&
    isTrialExpiryWindow(currentExpiryValue, options.profileCreatedAtValue, options.trialDays ?? 5);

  return extendSubscriptionExpiration(
    shouldResetFromPurchaseTime ? null : currentExpiryValue,
    daysToGrant,
    purchaseMs
  );
}

export function getSubscriptionSnapshot(
  profile?: SubscriptionLike | null,
  nowInput: number | Date = Date.now()
) {
  const nowMs = nowInput instanceof Date ? nowInput.getTime() : nowInput;
  const rawPlan = normalizeSubscriptionPlan(profile?.subscription_plan);
  const legacyRemainingDays = Math.max(0, Number(profile?.subscription_days_remaining) || 0);
  const expiresAtMs = toTimestamp(profile?.subscription_expires_at);
  const isOwner = profile?.role === 'admin' || profile?.is_super_admin === true;

  let hasPaidAccess = false;
  let remainingDays = 0;
  let remainingMs = 0;

  if (expiresAtMs) {
    remainingMs = Math.max(0, expiresAtMs - nowMs);
    hasPaidAccess = rawPlan !== 'free' && remainingMs > 0;
    remainingDays = hasPaidAccess ? Math.ceil(remainingMs / DAY_IN_MS) : 0;
  } else {
    hasPaidAccess = rawPlan !== 'free' && legacyRemainingDays > 0;
    remainingDays = hasPaidAccess ? legacyRemainingDays : 0;
  }

  const effectivePlan = isOwner ? rawPlan : hasPaidAccess ? rawPlan : 'free';
  const isFree = !isOwner && effectivePlan === 'free';
  const isStarter = !isOwner && effectivePlan === 'starter';
  const isPro = !isOwner && effectivePlan === 'pro';
  const hasStarterAccess = isOwner || isStarter || isPro;
  const hasMonitorAccess = isOwner || isPro;
  const isExpiringSoon =
    !isOwner && hasPaidAccess && (expiresAtMs ? remainingMs <= 5 * DAY_IN_MS : remainingDays <= 5);

  return {
    isOwner,
    rawPlan,
    effectivePlan,
    planLabel: getSubscriptionPlanLabel(effectivePlan),
    isPro,
    isStarter,
    isFree,
    hasPaidAccess,
    hasStarterAccess,
    hasMonitorAccess,
    remainingDays,
    remainingMs,
    isExpiringSoon,
    hasPreciseExpiry: Boolean(expiresAtMs),
    expiresAt: expiresAtMs ? new Date(expiresAtMs).toISOString() : null,
  };
}
